import React, { FC } from 'react';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import { DateTime } from 'luxon';
import { useButton, useDispatch, useI18n, useSelector } from '~Hooks';
import Icon from '~ComponentsImproved/Icon';
import Accordion from '~ComponentsImproved/Accordion';
import { BlackButton, BlueButton, Box, Button } from '~Styles';
import PrintErrorIcon from '~Assets/icons/print_error.svg';
import { PrintInQueue } from '~reducers';
import Heading from '~ComponentsImproved/Typography/Heading';
import { formatPrinterError } from '~utilities';
import { STATUS_COLORS } from '../constants';
import * as actions from '~actions';
import { PrintContent } from '../styles';
import styled from '@emotion/styled';
import { handyBreakpoint } from '~Styles/breakpoints';

const Base64Wrapper = styled.div`
  background: #fff;
  padding: 8px;
  box-sizing: border-box;

  img {
    width: 400px;
  }
`;

const ButtonWrapper = styled(Box)`
  ${handyBreakpoint} {
    flex-wrap: wrap;

    & > button:last-of-type {
      flex: 1;
    }
  }
`;

interface Props {
  print: PrintInQueue;
  onPrinterChange: (id: string) => void;
}

const ErrorRow: FC<Props> = ({ print, onPrinterChange }) => {
  const { i18n } = useI18n();

  const dispatch = useDispatch();

  const printers = useSelector((state) => state.printers);
  const printErrors = useSelector((state) =>
    state.POSPrintQueue.filter((print) => Boolean(print.error)),
  );

  const reprintSuccessful = !printErrors.find((p) => p.reprintOfPrintId === print.id);

  const orderIdFromPrint = print.orderId
    ? i18n('PrinterStatus.OrderId') + ': ' + print.orderId
    : undefined;

  const retryButton = useButton({
    onClick: () => {
      dispatch(actions.printQueue(print.id));
    },
  });

  const retryWithButton = useButton({
    onClick: () => {
      onPrinterChange(print.id);
    },
  });

  const dismissButton = useButton({
    onClick: () => {
      dispatch(actions.removePrintFromQueue(print.id));
    },
  });

  return (
    <Accordion
      initiallyOpen={print.printType === 'BONG'}
      preventMaxHeight
      title={
        <Box display="flex" alignItems="center" gap="xs">
          <Icon src={PrintErrorIcon} height="32px" width="32px" color="var(--colorUIRed)" />
          <Box display="flex" flexDirection="column" alignItems="flex-start">
            <Paragraph level={5}>
              {`${DateTime.fromJSDate(print.lastPrinted!).toFormat('HH:mm')} - ${i18n(
                'PrintType.' + print.printType,
              )}`}
            </Paragraph>
            <Heading level={4}>{formatPrinterError(print.error, i18n)}</Heading>
          </Box>
        </Box>
      }
      dark={false}>
      <Box display="flex" flexDirection="column" gap="xs">
        <PrintContent>
          {Array.isArray(print.data) ? (
            !!print.data.length &&
            print.data.map((row, i) => <div key={`${print.id}-content-${i}`}>{row.content}</div>)
          ) : print.base64Image ? (
            <Base64Wrapper>
              <img src={print.base64Image} alt="Print Preview" />
            </Base64Wrapper>
          ) : (
            <div>{print.data}</div>
          )}
        </PrintContent>

        {print.reprintedOnPrimary && (
          <Paragraph
            level={4}
            color={
              reprintSuccessful === undefined
                ? 'var(--colorBlueGray1200)'
                : reprintSuccessful
                ? STATUS_COLORS.OK
                : STATUS_COLORS.ERROR
            }>
            {reprintSuccessful ? i18n('Printer.BackupBong') : i18n('Printer.BackupBong.Failed')}
          </Paragraph>
        )}

        {orderIdFromPrint && (
          <Paragraph level={5} color="var(--colorBlueGray1200)">
            {orderIdFromPrint}
          </Paragraph>
        )}
      </Box>

      <ButtonWrapper display="flex" justifyContent="flex-end" gap="xs">
        <Button {...dismissButton}>{i18n('PrinterStatus.Errors.Dismiss')}</Button>
        <Button {...retryButton}>{i18n('PrinterStatus.Errors.Retry')}</Button>
        {printers.length > 0 && (
          <Button {...retryWithButton}>{i18n('PrinterStatus.Errors.RetryWith')}</Button>
        )}
      </ButtonWrapper>
    </Accordion>
  );
};

export default ErrorRow;
